/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { useEffect } from 'react';

/**
 * Attaches event listeners for both mouse and touch
 * to fire a handler whenever a click (mousedown) is
 * detected outside the DOM element.
 *
 * @param ref The element that when clicked outside of,
 * the handler is fired.
 * @param handler The handler to fire when a click outside
 * the ref is detected.
 * @param checker An optional additional function that returns
 * a boolean which is then and-ed with the check. If this
 * function returns true, then the handler will not be fired.
 * This is useful in cases where there exceptions to the elements
 * outside the ref element to not apply the handler on.
 */
export const useOnClickOutside = <T extends HTMLElement>(
    ref: React.MutableRefObject<T>,
    handler: (event: MouseEvent) => void,
    checker: (target: HTMLElement) => boolean = () => false
) => {
    useEffect(() => {
        const listener = (event: MouseEvent) => {
            const targetElement = <HTMLElement>event.target;
            // Do nothing if clicking ref's element or descendent elements
            if (ref && (!ref.current || ref.current.contains(<HTMLElement>event.target) || checker(targetElement))) {
                return;
            }

            handler(event);
        };

        document.addEventListener('mousedown', listener);
        document.addEventListener('touchstart', listener);

        return () => {
            document.removeEventListener('mousedown', listener);
            document.removeEventListener('touchstart', listener);
        };
    }, [ref, handler, checker]);
};
